import React, { useState, useCallback, useEffect } from "react";

import {
    Box,
    Divider,
    TextField,
    Grid,
    Button,
    Switch,
    FormControlLabel,
    styled,
    Card,
    List,
    ListSubheader,
    ListItem,
    ListItemButton,
    IconButton,
    Skeleton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText
} from '@mui/material';

import { useParams } from "react-router-dom";
import { useFormik } from 'formik';
import { grey, red } from "@mui/material/colors";
import { checkValueExists } from "../../../utils/dataSchema";

import { useLocations, locationTypes, targetTypes } from "../../../hooks/api/useLocations";
import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useAppContextFeatures } from "../../../context/AppContext";
import { useRegions, regionTypes } from "../../../hooks/api/useRegions";
import { useInventories, inventoriesTypes, dataTypes as inventoriesDataTypes } from "../../../hooks/api/useInventories";

import { validationSchema } from "../../../schema/locations/locationSchema";
// import { initialValues: initialRackValues, validationSchema: validateRackSchame } from "../../../schema/warehouse/rackSchema";
import CommonBreadcumbs from "../../common/CommonBreadcumbs";
import CommonSectionHeader from "../../common/CommonSectionHeader";
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { useTranslations } from "../../../hooks/useTranslations";

import { CommonIcons, icons } from "../../common/CommonIcons";

import NewRack from "../racks/NewRack"
import UpdateRack from "../racks/UpdateRack";
import NewSlot from "../slots/NewSlot";
import UpdateSlot from "../slots/UpdateSlot";
import NewZone from "../zones/NewZone"
import UpdateZone from "../zones/UpdateZone";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

const StyledCard2 = styled(Card)(({ theme }) => ({
    padding: theme.spacing(0)
}));

const StyledListHeader = styled(ListSubheader)(({ theme }) => ({
    backgroundColor: grey[100],
    textTransform: 'capitalize',
}));

export default function UpdateLocation({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@location"] })

    const { id } = useParams();
    const { promptMessage } = useSystemPrompt();
    const { setSystemLoading, openCustomDrawer, closeCustomDrawer } = useAppContextFeatures();

    // const navigate = useNavigate();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const { list, update, get, remove, filterOperatorTypes } = useLocations({ locationType: locationTypes.location, targetType: targetTypes.location });
    const { list: listInventories } = useInventories({ inventoriesType: inventoriesTypes.inventory });
    const { list: listRegions } = useRegions({});

    const [data, setData] = useState()
    const [dataInventories, setDataInventories] = useState([])
    const [dataCountries, setDataCountries] = useState([])
    const [dataStates, setDataStates] = useState([])
    const [dataAreas, setDataAreas] = useState([])
    const [dataZones, setDataZones] = useState([])

    const [loadingChildren, setLoadingChildren] = useState(false)

    const [child, setChild] = useState({
        racks: [],
        slots: [],
    })

    const [selectedId, setSelectedId] = useState("")

    const onSubmit = async (currentValues) => {

        setSystemLoading(true)

        try {

            let data = {
                location: {
                    location_name: currentValues?.locationName,
                    location_code: currentValues?.locationCode,
                    person_name: currentValues?.picName,
                    person_contact: currentValues?.contactno,
                    person_email: currentValues?.email,
                    address_1: currentValues?.address,
                    address_2: currentValues?.address2,
                    postcode: currentValues?.postcode,
                    state_id: currentValues?.stateId,
                    country_id: currentValues?.countryId,
                    area_id: currentValues?.areaId,
                    is_default: currentValues?.defaultStatus ? 1 : 0,
                    status: currentValues?.status ? 1 : 0
                }
            }

            const response = await update({ id, data });

            promptMessage(true, `${response?.message}`)

        } catch (error) {
            promptMessage(false, `Action Failed (${error?.message})`)
        }

        setSystemLoading(false)

    }

    const fetchChildren = async (id) => {

        if (!id) return false

        // setSystemLoading(true)
        setLoadingChildren(true)

        try {

            const props = {
                limit: -1,
                skip: 0,
                sortInfo: {
                    id: "id",
                    dir: 0,
                },
                filterValue: [
                    {
                        name: "rack_id",
                        operator: filterOperatorTypes.eq,
                        value: id
                    }
                ]
            };

            props.locationType = locationTypes.slot
            props.parentId = id

            const { data } = await list(props)
            const { slots } = data;

            setChild(prev => ({
                ...prev,
                slots
            }))

            setSelectedId(id)

        } catch (error) {
            // promptMessage(false, `${error?.message}`)
        }

        // setSystemLoading(false)
        setLoadingChildren(false)

        return true;

    }

    const formik = useFormik({
        initialValues: data,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;

    const fetchDataCountry = async () => {

        try {

            const props = {
                limit: -1,
                skip: 0,
                filterValue: [],
                sortInfo: {
                    id: "country_name",
                    dir: 0,
                }
            }

            props.regionType = regionTypes.country

            const { data } = await listRegions(props)
            setDataCountries(data?.countries)

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

    }

    const fetchDataStates = async (countryId) => {

        setDataStates([])

        try {

            const props = {
                limit: -1,
                skip: 0,
                filterValue: [],
                sortInfo: {
                    id: "state_name",
                    dir: 0,
                }
            }

            props.regionType = regionTypes.state
            props.countryId = countryId

            const { data } = await listRegions(props)

            setDataStates(data?.states)

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

    }

    const fetchDataAreas = async (stateId) => {

        setDataAreas([])

        try {

            const props = {
                limit: -1,
                skip: 0,
                filterValue: [],
                sortInfo: {
                    id: "area_name",
                    dir: 0,
                }
            }

            props.regionType = regionTypes.area
            props.stateId = stateId

            const { data } = await listRegions(props)

            setDataAreas(data?.areas)

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }
    }

    const fetchDataZone = async () => {

        // console.log("Zone", data)

        try {

            const props = {
                limit: -1,
                skip: 0,
                filterValue: [],
                sortInfo: {
                    id: "location_id",
                    dir: 0,
                }
            }

            props.locationType = locationTypes.zone
            props.parentId = id

            const { data } = await list(props)

            setDataZones(data?.zones)

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

    }

    const loadData = async (id) => {

        setSystemLoading(true)

        try {

            const { data } = await get({ id })
            const { location } = data;

            setData({
                locationName: location?.location_name,
                locationCode: location?.location_code,
                picName: location?.person_name ? location.person_name : "",
                contactno: location?.person_contact ? location.person_contact : "",
                email: location?.person_email ? location.person_email : "",
                address: location?.address_1 ? location?.address_1 : "",
                address2: location?.address_2 ? location?.address_2 : "",
                postcode: location?.postcode,
                areaId: location?.area_id,
                countryId: location?.country_id,
                stateId: location?.state_id,
                status: location?.status === '1',
                defaultStatus: location?.is_default === '1',
            })

            const props = {
                limit: -1,
                skip: 0,
                sortInfo: {
                    id: "id",
                    dir: 0,
                },
                filterValue: [
                    {
                        name: "location_id",
                        operator: filterOperatorTypes.eq,
                        value: id
                    }
                ]
            };

            props.locationType = locationTypes.rack
            props.parentId = id

            const { data: data2 } = await list(props)
            const { racks } = data2;

            setChild(prev => ({
                ...prev, racks
            }))

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

    }

    useEffect(() => {

        loadData(id);
        fetchDataCountry();
        fetchDataZone();

    }, [])

    useEffect(() => {

        if (values?.countryId) {
            if (dataStates.length) {
                if (dataStates[0].country_id !== values.countryId) {
                    fetchDataStates(values.countryId)
                }
            } else {
                fetchDataStates(values.countryId)
            }
        }

        if (values?.stateId) {
            if (dataAreas.length) {
                if (dataAreas[0].state_id !== values.stateId) {
                    fetchDataAreas(values.stateId)
                }
            } else {
                fetchDataAreas(values.stateId)
            }
        }

    }, [values])

    const checkPostcodeExists = () => {
        const checkExists = getPostcodesArray().find(i => i === values.postcode)

        if (checkExists) return values.postcode

        return ""
    }

    const getPostcodesArray = () => {

        let postcodeStringCheck = dataAreas.filter(i => i.id === formik.values.areaId)

        if (postcodeStringCheck.length <= 0) return []

        let postcodeString = postcodeStringCheck.length >= 0 ? postcodeStringCheck[0].postcode_string : '';

        let postcodeArray = (postcodeString.includes(",")) ? postcodeString.split(",") : [postcodeString]

        if (postcodeArray.length <= 0) return []

        return postcodeArray
    }

    const getUpdateTitleName = (data) => {

        let titleName = ""

        const { locationType } = data

        if (locationType === locationTypes.rack) {
            titleName = t("updateRack")

        } else if (locationType === locationTypes.slot) {
            titleName = t("updateSlot")
        } else {
            titleName = t("updateZone")
        }

        return titleName

    }

    const getAddNewTitleName = (data) => {

        let titleName = ""

        const { locationType } = data

        if (locationType === locationTypes.rack) {
            titleName = t("newRack")

        } else if (locationType === locationTypes.slot) {
            titleName = t("newSlot")
        } else {
            titleName = t("newZone")
        }

        return titleName

    }

    const getEditComponent = (props) => {

        const { locationType, itemId, parentId } = props

        if (locationType === locationTypes.rack) {
            return (
                <UpdateRack
                    id={itemId}
                    parentId={parentId}
                    handleOnSuccess={() => {
                        closeCustomDrawer()
                        loadData(id)
                        if (selectedId)
                            fetchChildren(selectedId)
                    }}
                />
            )
        }

        if (locationType === locationTypes.slot) {
            return (
                <UpdateSlot
                    id={itemId}
                    parentId={parentId}
                    handleOnSuccess={() => {
                        closeCustomDrawer()
                        loadData(id)
                        if (selectedId)
                            fetchChildren(selectedId)
                    }}
                />
            )
        }

        return (
            <UpdateZone
                id={itemId}
                parentId={parentId}
                handleOnSuccess={() => {
                    closeCustomDrawer()
                    // loadData(id)
                    fetchDataZone()
                }}
            />
        )

    }

    const getAddNewComponent = (props) => {

        const { locationType, parentId } = props

        if (locationType === locationTypes.rack) {
            return (
                <NewRack
                    parentId={parentId}
                    handleOnSuccess={() => {
                        closeCustomDrawer()
                        loadData(id)
                        if (selectedId)
                            fetchChildren(selectedId)
                    }}
                />
            )
        }

        if (locationType === locationTypes.slot) {
            return (
                <NewSlot
                    parentId={parentId}
                    handleOnSuccess={() => {
                        closeCustomDrawer()
                        loadData(id)
                        if (selectedId)
                            fetchChildren(selectedId)
                    }}
                />
            )
        }

        return (
            <NewZone
                parentId={parentId}
                handleOnSuccess={() => {
                    closeCustomDrawer()
                    // loadData(id)
                    fetchDataZone()
                }}
            />
        )

    }

    const handleItemEdit = ({ locationType = '', itemId, parentId }) => {

        if (!locationType) return false;

        const title = getUpdateTitleName({ locationType })

        openCustomDrawer({
            show: true,
            anchor: "right",
            // title: `${locationType === locationTypes.rack? "Update Rack" : "Update Slot"}`,
            title,
            PaperProps: {
                sx: { width: 2 / 3 }
            },
            handleOnClose: () => { },
            ComponentActions:
                <></>,
            ComponentBody: getEditComponent({ locationType, itemId, parentId })
        })

        return true;

    }

    const handleItemNew = ({ locationType = '', parentId }) => {

        if (!locationType) return false

        const title = getAddNewTitleName({ locationType })

        openCustomDrawer({
            show: true,
            anchor: "right",
            title,
            PaperProps: {
                sx: { width: 2 / 3 }
            },
            handleOnClose: () => { },
            ComponentActions:
                <></>,
            ComponentBody: getAddNewComponent({ locationType, parentId })

        })

        return true;

    }

    // get for inventories whether have utilize before
    const getInventories = async ({ rackId, slotId }) => {

        setSystemLoading(true)

        try {

            let filterValue = [];

            if (rackId) {
                filterValue.push({
                    name: "rack_id",
                    operator: filterOperatorTypes.eq,
                    value: rackId
                })
            }

            if (slotId) {
                filterValue.push({
                    name: "slot_id",
                    operator: filterOperatorTypes.eq,
                    value: slotId
                })
            }

            let props = {
                limit: -1,
                skip: 0,
                sortInfo: {
                    id: "id",
                    dir: 0,
                },
                filterValue
            };

            props.dataTypes = [inventoriesDataTypes.groupSlotBatch]

            const { data } = await listInventories(props)

            setSystemLoading(false)

            return data?.inventories ? data.inventories : []

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

        return true;

    }

    const handleItemDelete = async ({ itemId, targetType }) => {

        setSystemLoading(true)

        try {

            // check allow to delete - for racks
            if (targetType === targetTypes.rack) {
                const checkUtilize = await getInventories({ rackId: itemId })
                // console.log('check', checkUtilize)
                if (checkUtilize.length > 0) {
                    throw new Error(`Rack ${checkUtilize.length} entity utilized`)
                }
            }

            // check allow to delete - for slot
            if (targetType === targetTypes.slot) {
                const checkUtilize = await getInventories({ slotId: itemId })
                // console.log('check', checkUtilize)
                if (checkUtilize.length > 0) {
                    throw new Error(`Slot ${checkUtilize.length} entity utilized`)
                }
            }

            let result = await remove({ ids: [itemId], targetType })
            promptMessage(true, result?.message)

            if (targetType === targetTypes.zone) {
                fetchDataZone()
            } else { // for item or rack or slot
                loadData(id)
            }

            if (selectedId)
                fetchChildren(selectedId)

        } catch (error) {
            promptMessage(false, `Action Failed (${error?.message})`)
        }

        setSystemLoading(false)

    }

    const RenderListDataItemSkeleton = () => {
        return (
            <ListItem
                disablePadding
                divider
            >
                <Skeleton
                    animation="wave"
                    // variant="circular" 
                    sx={{
                        // minWidth: 100,
                        width: '100%',
                        // mr: 1,
                        height: 40,
                        mx: 1
                    }}
                />
            </ListItem>
        )
    }

    const handleRackClick = ({ id }) => {
        fetchChildren(id)
    }

    const RenderRackNotSelected = () => {
        return (
            <StyledCard2>
                <Box
                    sx={{
                        width: '100%',
                        position: 'relative',
                        overflow: 'auto',
                        minHeight: 300,
                    }}
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"center"}
                    alignItems={"center"}
                >
                    <Box>
                        {t('youHaveNotSelectAnyItem')}
                    </Box>
                </Box>
            </StyledCard2>
        )
    }

    const RenderEmpty = ({ locationType = '', parentId }) => {

        if (!locationType) return <></>

        return (
            <StyledCard2>
                <Box
                    sx={{
                        width: '100%',
                        position: 'relative',
                        overflow: 'auto',
                        minHeight: 300,
                    }}
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"center"}
                    alignItems={"center"}
                >
                    <Box>
                        {t('youHaveNotCreatedAnyItemYet')}
                    </Box>
                    <Button
                        variant="outlined"
                        sx={{
                            mt: 1
                        }}
                        onClick={() => handleItemNew({ locationType, parentId })}
                    >
                        {t('createNew')}
                    </Button>
                </Box>
            </StyledCard2>
        )
    }

    const RenderRacks = () => {

        if (!child.racks.length) return <RenderEmpty locationType={locationTypes.rack} parentId={id} />

        return (
            <StyledCard2>
                <List
                    sx={{
                        width: '100%',
                        position: 'relative',
                        overflow: 'auto',
                        minHeight: 300,
                        '& ul': { padding: 0 },
                    }}
                    // subheader={<li />}
                    disablePadding
                >
                    <StyledListHeader>{t("racks")}</StyledListHeader>
                    {
                        child.racks.map((i, index) => (
                            <ListItem
                                key={`${index}`}
                                disablePadding
                                divider
                            >
                                <ListItemButton
                                    disableGutters
                                    sx={{
                                        px: 2
                                    }}
                                    onClick={() => handleRackClick({ id: i.id })}
                                    selected={
                                        selectedId === i.id
                                    }
                                >
                                    <Grid container spacing={1}>
                                        <Grid item md={11} sm={11} xs={11} display={"flex"} justifyContent={"flex-start"} alignItems={"center"}>
                                            {`${i?.rack_name}`}

                                            {
                                                i?.status !== "1" &&
                                                <Box sx={{ ml: 1 }}>
                                                    <CommonIcons name={icons.disabled} sx={{ color: red[600] }} />
                                                </Box>
                                            }

                                            {
                                                i?.is_default === "1" &&
                                                <Box sx={{ ml: 1 }}>
                                                    <CommonIcons name={icons.default} />
                                                </Box>
                                            }


                                        </Grid>
                                        <Grid item md={1} sm={1} xs={1} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                                            <IconButton
                                                color={"primary"}
                                                size="small"
                                                onClick={() => handleItemEdit({ locationType: locationTypes.rack, itemId: i.id, parentId: i.location_id })}
                                            >
                                                <CommonIcons name={icons.edit} />
                                            </IconButton>

                                            <IconButton
                                                color={"error"}
                                                size="small"
                                                onClick={() => handleItemDelete({ itemId: i.id, targetType: targetTypes.rack })}
                                                sx={{
                                                    mr: 2
                                                }}
                                            >
                                                <CommonIcons name={icons.remove} />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                </ListItemButton>
                            </ListItem>
                        ))
                    }
                </List>

                <Box
                    sx={{ m: 1 }}
                >
                    <Button
                        variant={"outlined"}
                        size="medium"
                        fullWidth
                        onClick={() => handleItemNew({ locationType: locationTypes.rack, parentId: id })}
                    >
                        <CommonIcons name={icons.add} />
                        <Box sx={{ ml: 1 }}>
                            New
                        </Box>
                    </Button>
                </Box>
            </StyledCard2>
        )
    }

    const RenderSlots = () => {

        // check is loading
        if (loadingChildren) {
            return (
                <StyledCard2>
                    <List
                        sx={{
                            width: '100%',
                            position: 'relative',
                            overflow: 'auto',
                            // height: '100%',
                            minHeight: 300,
                            '& ul': { padding: 0 },
                        }}
                        // subheader={<li />}
                        disablePadding
                    >
                        <StyledListHeader>{`Slot`}</StyledListHeader>
                        {
                            [0, 1, 2, 3].map((i, index) => {
                                return (
                                    <RenderListDataItemSkeleton key={index} />
                                )
                            })
                        }
                    </List>
                </StyledCard2>
            )
        }


        if (!child.racks.length) return <></>
        if (!selectedId) return <RenderRackNotSelected />
        if (!child.slots.length) return <RenderEmpty locationType={locationTypes.slot} parentId={selectedId} />

        return (
            <StyledCard2>
                <List
                    sx={{
                        width: '100%',
                        // backgroundColor: 'red',
                        // maxWidth: 360,
                        // bgcolor: 'background.paper',
                        position: 'relative',
                        overflow: 'auto',
                        minHeight: 300,
                        '& ul': { padding: 0 },
                    }}
                    // subheader={<li />}
                    disablePadding
                >
                    <StyledListHeader>{`Slots`}</StyledListHeader>
                    {
                        child.slots.map((i, index) => (
                            <ListItem
                                key={`${index}`}
                                disablePadding
                                divider
                            >
                                <ListItemButton
                                    disableGutters
                                    sx={{
                                        px: 2
                                    }}
                                >
                                    <Grid container spacing={1}>
                                        <Grid item md={11} sm={11} xs={11} display={"flex"} justifyContent={"flex-start"} alignItems={"center"}>
                                            {`${i.slot_name}`}

                                            {
                                                i?.status !== "1" &&
                                                <Box sx={{ ml: 1 }}>
                                                    <CommonIcons name={icons.disabled} sx={{ color: red[600] }} />
                                                </Box>
                                            }

                                            {
                                                i?.is_default === "1" &&
                                                <Box sx={{ ml: 1 }}>
                                                    <CommonIcons name={icons.default} />
                                                </Box>
                                            }
                                        </Grid>
                                        <Grid item md={1} sm={1} xs={1} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                                            <IconButton
                                                color={"primary"}
                                                size="small"
                                                onClick={() => handleItemEdit({ locationType: locationTypes.slot, itemId: i.id, parentId: i.rack_id })}
                                            >
                                                <CommonIcons name={icons.edit} />
                                            </IconButton>

                                            <IconButton
                                                color={"error"}
                                                size="small"
                                                onClick={() => handleItemDelete({ itemId: i.id, targetType: targetTypes.slot })}
                                                sx={{
                                                    mr: 2
                                                }}
                                            >
                                                <CommonIcons name={icons.remove} />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                </ListItemButton>
                            </ListItem>
                        ))
                    }
                </List>
                <Box
                    sx={{ m: 1 }}
                >
                    <Button
                        variant={"outlined"}
                        size="medium"
                        fullWidth

                        onClick={() => handleItemNew({ locationType: locationTypes.slot, parentId: selectedId })}
                    >
                        <CommonIcons name={icons.add} />
                        <Box sx={{ ml: 1 }}>
                            New
                        </Box>
                    </Button>
                </Box>
            </StyledCard2>
        )
    }

    const RenderZone = () => {

        if (!dataZones.length) return <RenderEmpty locationType={locationTypes.zone} parentId={id} />

        return (
            <StyledCard2>
                <List
                    sx={{
                        width: '100%',
                        position: 'relative',
                        overflow: 'auto',
                        minHeight: 300,
                        '& ul': { padding: 0 },
                    }}
                    // subheader={<li />}
                    disablePadding
                >
                    <StyledListHeader>{"Zones"}</StyledListHeader>
                    {
                        dataZones.map((i, index) => (
                            <ListItem
                                key={`${index}`}
                                disablePadding
                                divider
                            >
                                <ListItemButton
                                    disableGutters
                                    sx={{
                                        px: 2
                                    }}
                                    selected={
                                        selectedId === i.id
                                    }
                                >
                                    <Grid container spacing={1}>
                                        <Grid item md={11} sm={11} xs={11} display={"flex"} justifyContent={"flex-start"} alignItems={"center"}>
                                            {`${i?.zone_name}`}

                                            {
                                                i?.status !== "1" &&
                                                <Box sx={{ ml: 1 }}>
                                                    <CommonIcons name={icons.disabled} sx={{ color: red[600] }} />
                                                </Box>
                                            }

                                            {
                                                i?.is_default === "1" &&
                                                <Box sx={{ ml: 1 }}>
                                                    <CommonIcons name={icons.default} />
                                                </Box>
                                            }

                                        </Grid>
                                        <Grid item md={1} sm={1} xs={1} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                                            <IconButton
                                                color={"primary"}
                                                size="small"
                                                onClick={() => handleItemEdit({ locationType: locationTypes.zone, itemId: i.id, parentId: i.location_id })}
                                            >
                                                <CommonIcons name={icons.edit} />
                                            </IconButton>

                                            <IconButton
                                                color={"error"}
                                                size="small"
                                                onClick={() => handleItemDelete({ itemId: i.id, targetType: targetTypes.zone })}
                                                sx={{
                                                    mr: 2
                                                }}
                                            >
                                                <CommonIcons name={icons.remove} />
                                            </IconButton>

                                        </Grid>
                                    </Grid>
                                </ListItemButton>
                            </ListItem>
                        ))
                    }
                </List>

                <Box
                    sx={{ m: 1 }}
                >
                    <Button
                        variant={"outlined"}
                        size="medium"
                        fullWidth
                        onClick={() => handleItemNew({ locationType: locationTypes.zone, parentId: id })}
                    >
                        <CommonIcons name={icons.add} />
                        <Box sx={{ ml: 1 }}>
                            {t('new')}
                        </Box>
                    </Button>
                </Box>
            </StyledCard2>
        )
    }

    const RenderRackHierachy = () => {

        return (
            <Grid container spacing={0}>

                <Grid item md={5.5} sm={12} xs={12}>
                    {
                        RenderRacks()
                    }
                </Grid>

                <Grid item md={1} sm={12} xs={12} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                    <CommonIcons name={icons.chevronRight} />
                </Grid>

                <Grid item md={5.5} sm={12} xs={12}>
                    {
                        RenderSlots()
                    }
                </Grid>

            </Grid>
        )
    }

    const RenderZoneList = () => {

        return (
            <Grid container spacing={0}>

                <Grid item md={12} sm={12} xs={12}>
                    {
                        RenderZone()
                    }
                </Grid>

            </Grid>
        )
    }

    const RenderStorage = () => {

        if (!values) return <></>

        return (
            <StyledCard
                sx={{
                    mb: 2
                }}
            >
                <Grid container spacing={0.5}>

                    <Grid item xs={12} sm={12} md={3} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t('storages')}
                            subTitle={t("defineForAvailableStorageSections")}
                        />

                    </Grid>

                    <Grid item xs={12} sm={12} md={9}>
                        {
                            RenderRackHierachy()
                        }
                    </Grid>
                </Grid>
            </StyledCard>
        )
    }

    const RenderLocationZone = () => {

        if (!values) return <></>

        return (
            <StyledCard>
                <Grid container spacing={0.5}>

                    <Grid item xs={12} sm={12} md={3} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t('segmentation')}
                            subTitle={t("organizeLocationSections")}
                        />

                    </Grid>

                    <Grid item xs={12} sm={12} md={9}>
                        {
                            RenderZoneList()
                        }
                    </Grid>
                </Grid>
            </StyledCard>
        )
    }

    const RenderForm = () => {

        if (!values) return <></>

        return (

            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={0.5}>
                        <Grid item xs={12} sm={12} md={3} textAlign={"left"}>

                            <CommonSectionHeader
                                title={t('warehouseLocation')}
                                subTitle={t(`basicInformationForWarehouse`)}
                            />

                        </Grid>

                        <Grid item xs={12} sm={12} md={9}>
                            <Grid container spacing={0.5}>
                                <Grid item xs={6} sm={6} md={6}
                                    sx={{
                                        display: 'center',
                                    }}>
                                    <FormControlLabel
                                        sx={{
                                            marginLeft: '1px'
                                        }}
                                        control={
                                            <Switch
                                                name="packageStatus"
                                                checked={Boolean(values?.status)}
                                                onChange={(event, checked) => {
                                                    setFieldValue("status", checked);
                                                }}
                                            />
                                        }
                                        label={
                                            Boolean(values?.status) ? t("enabled") : t("disabled")
                                        }
                                    />
                                </Grid>
                                {/* <Grid item xs={6} sm={6} md={6}
                                    sx={{
                                        display:'center',
                                    }}>
                                    <FormControlLabel 
                                        sx={{
                                            marginLeft:'1px'
                                        }}
                                        control={
                                            <Switch 
                                                name="packageStatus" 
                                                checked={Boolean(values?.defaultStatus)}
                                                onChange={(event, checked) => {
                                                    setFieldValue("defaultStatus", checked);
                                                }}
                                            />
                                        }     
                                        label={
                                            Boolean(values?.defaultStatus)? "Default" : "Not Default"
                                        }
                                    />
                                </Grid> */}
                                <Grid item xs={12} sm={12} md={6}>
                                    <></>
                                </Grid>

                                <Grid item xs={12} sm={12} md={4}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        autoFocus
                                        fullWidth
                                        name={"locationName"}
                                        label={t("locationName")}
                                        type="text"
                                        variant="outlined"
                                        value={values.locationName}
                                        onChange={handleChange}
                                        error={touched.locationName && Boolean(errors.locationName)}
                                        helperText={touched.locationName && errors.locationName}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={2}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        autoFocus
                                        fullWidth
                                        name={"locationCode"}
                                        label={t("locationCode")}
                                        type="text"
                                        variant="outlined"
                                        value={values.locationCode}
                                        onChange={handleChange}
                                        error={touched.locationCode && Boolean(errors.locationCode)}
                                        helperText={touched.locationCode && errors.locationCode}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"picName"}
                                        label={t("personInChargeName")}
                                        type="text"
                                        variant="outlined"
                                        value={values.picName}
                                        onChange={handleChange}
                                        error={touched.picName && Boolean(errors.picName)}
                                        helperText={touched.picName && errors.picName}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"contactno"}
                                        label={t("contact")}
                                        type="text"
                                        variant="outlined"
                                        value={values.contactno}
                                        onChange={handleChange}
                                        error={touched.contactno && Boolean(errors.contactno)}
                                        helperText={touched.contactno && errors.contactno}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"email"}
                                        label={t("email")}
                                        type="text"
                                        variant="outlined"
                                        value={values.email}
                                        onChange={handleChange}
                                        error={touched.email && Boolean(errors.email)}
                                        helperText={touched.email && errors.email}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                        </Grid>

                        <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                            <CommonSectionHeader
                                title={t('address')}
                                subTitle={t(`preciseLocationInformation`)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={9}>
                            <Grid container spacing={0.5}>
                                <Grid item xs={12} sm={12} md={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        rows={2}
                                        multiline
                                        name={"address"}
                                        label={t("address1")}
                                        type="text"
                                        variant="outlined"
                                        value={values.address}
                                        onChange={handleChange}
                                        error={touched.address && Boolean(errors.address)}
                                        helperText={touched.address && errors.address}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        rows={2}
                                        multiline
                                        name={"address2"}
                                        label={t("address2")}
                                        type="text"
                                        variant="outlined"
                                        value={values?.address2 ? values.address2 : ""}
                                        onChange={handleChange}
                                        error={touched.address2 && Boolean(errors.address2)}
                                        helperText={touched.address2 && errors.address2}
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <FormControl
                                        margin="dense"
                                        fullWidth
                                        size="small"
                                        error={touched.countryId && Boolean(errors.countryId)}
                                    >
                                        <InputLabel id="country">{t("country")}</InputLabel>
                                        <Select
                                            labelId="countryId"
                                            label={t("country")}
                                            name={"countryId"}
                                            onChange={(event) => {
                                                setFieldValue(`countryId`, event.target.value)
                                                setFieldValue(`stateId`, "")
                                                setFieldValue(`areaId`, "")
                                                setDataStates([])
                                                setDataAreas([])
                                            }}
                                            value={
                                                checkValueExists({
                                                    dataSearch: dataCountries,
                                                    valueToCompare: values.countryId
                                                })
                                            }
                                            sx={{
                                                textAlign: 'left'
                                            }}
                                        >
                                            {
                                                dataCountries.map((i, index) => {
                                                    return (
                                                        <MenuItem key={index} value={i.id}>{i.country_name}</MenuItem>
                                                    )
                                                })
                                            }
                                        </Select>
                                        <FormHelperText>
                                            {touched.countryId && errors.countryId}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={6}>
                                    <FormControl
                                        margin="dense"
                                        fullWidth
                                        size="small"
                                        error={touched.stateId && Boolean(errors.stateId)}
                                    >
                                        <InputLabel id="state">{t('state')}</InputLabel>
                                        <Select
                                            labelId="stateId"
                                            label={t('state')}
                                            name={"stateId"}
                                            onChange={(event) => {
                                                setFieldValue(`stateId`, event.target.value)
                                                setFieldValue(`areaId`, "")
                                                setDataAreas([])
                                            }}
                                            value={
                                                checkValueExists({
                                                    dataSearch: dataStates,
                                                    valueToCompare: values.stateId
                                                })
                                            }
                                            sx={{
                                                textAlign: 'left'
                                            }}
                                        >
                                            {
                                                dataStates.map((i, index) => {
                                                    return (
                                                        <MenuItem key={index} value={i.id}>{i.state_name}</MenuItem>
                                                    )
                                                })
                                            }
                                        </Select>
                                        <FormHelperText>
                                            {touched.stateId && errors.stateId}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={12} md={6}>
                                    <FormControl
                                        margin="dense"
                                        fullWidth
                                        size="small"
                                        error={touched.areaId && Boolean(errors.areaId)}
                                    >
                                        <InputLabel id="area">{t('area')}</InputLabel>
                                        <Select
                                            labelId="areaId"
                                            label={t('area')}
                                            name={"areaId"}
                                            onChange={handleChange}
                                            value={
                                                checkValueExists({
                                                    dataSearch: dataAreas,
                                                    valueToCompare: values.areaId
                                                })
                                            }
                                            sx={{
                                                textAlign: 'left'
                                            }}
                                        >
                                            {
                                                dataAreas.map((i, index) => {
                                                    return (
                                                        <MenuItem key={index} value={i.id}>{i.area_name}</MenuItem>
                                                    )
                                                })
                                            }
                                        </Select>
                                        <FormHelperText>
                                            {touched.areaId && errors.areaId}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6} md={6}>
                                    <FormControl
                                        margin="dense"
                                        fullWidth
                                        size="small"
                                        error={touched.postcode && Boolean(errors.postcode)}
                                    >
                                        <InputLabel id="postcode">{t('postcode')}</InputLabel>
                                        <Select
                                            labelId="postcode"
                                            label={t('postcode')}
                                            name={"postcode"}
                                            onChange={handleChange}
                                            value={
                                                checkPostcodeExists()
                                            }
                                            sx={{
                                                textAlign: 'left'
                                            }}
                                        >

                                            {
                                                getPostcodesArray().length ?
                                                    (
                                                        getPostcodesArray().map((i, index) => {
                                                            return (
                                                                <MenuItem key={index} value={i}>{i}</MenuItem>
                                                            )
                                                        })
                                                    )
                                                    :
                                                    (
                                                        <MenuItem value={''}>{null}</MenuItem>
                                                    )
                                            }
                                        </Select>
                                        <FormHelperText>
                                            {touched.postcode && errors.postcode}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                            </Grid>
                        </Grid>
                    </Grid>
                </StyledCard>

                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <Button
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                        // loading={isLoading}
                        >
                            {t('saveChanges')}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.updateLocation })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t("updateLocation")}
                breadcrumbs={[
                    {
                        displayText: t("locations"),
                        props: {
                            href: getRoute("settings/locations")
                        }
                    },
                    {
                        displayText: t("updateLocation"),
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            {RenderForm()}

            {RenderStorage()}

            {RenderLocationZone()}

        </Box>
    )
}